import { useGeckoAnalytics } from 'features/analytics'
import { useScatterplotContext } from 'features/timeSeries'
import React, { useEffect, useMemo } from 'react'
import { EnhancedSensorEntry } from 'types'

interface UseMemoizedSensorValuesProps {
  enhancedSensorEntries: EnhancedSensorEntry[]
}

export const useMemoizedSensorValues = ({
  enhancedSensorEntries,
}: UseMemoizedSensorValuesProps) => {
  const { isLoading, liveTail, derivedValues, selectedSensors, timeRange } =
    useScatterplotContext()

  const { trackEvent } = useGeckoAnalytics()

  // Track previous values to detect live tail refreshes vs data changes
  const prevSelectedSensorsRef = React.useRef<string>(
    JSON.stringify(selectedSensors.map((s) => s.id).sort())
  )
  const prevDerivedValuesRef = React.useRef<string>(
    JSON.stringify(derivedValues.map((dv) => dv.id).sort())
  )
  const prevTimeRangeFromRef = React.useRef<string>(timeRange.from)

  // Determine if this is a live tail refresh (only timeRange.to changed, nothing else)
  const isLiveTailRefresh = useMemo(() => {
    if (!liveTail || !isLoading) {
      return false
    }

    const currentSelectedSensorIds = JSON.stringify(
      selectedSensors.map((s) => s.id).sort()
    )
    const currentDerivedValueIds = JSON.stringify(
      derivedValues.map((dv) => dv.id).sort()
    )

    const sensorsChanged =
      currentSelectedSensorIds !== prevSelectedSensorsRef.current
    const derivedValuesChanged =
      currentDerivedValueIds !== prevDerivedValuesRef.current
    const timeRangeFromChanged = timeRange.from !== prevTimeRangeFromRef.current

    // If only timeRange.to changed (detected by unchanged from), it's a live tail refresh
    return !sensorsChanged && !derivedValuesChanged && !timeRangeFromChanged
  }, [liveTail, isLoading, selectedSensors, derivedValues, timeRange.from])

  // Combine selected sensors with derived values for chart rendering
  const allSensorsToRender = useMemo(() => {
    const sensorsToRender: Array<{ id: string; name: string }> =
      selectedSensors.map((sensor) => ({
        id: sensor.id,
        name: sensor.data.name,
      }))

    // Add derived values as virtual sensors
    // Only add derived values if their dependent sensors are selected
    derivedValues.forEach((derived) => {
      const allDependenciesSelected = derived.data.dependencyIds.every(
        (sensorId) =>
          sensorId === 'timestamp' ||
          selectedSensors.some((selected) => selected.id === sensorId) ||
          derivedValues.some((derived) =>
            derived.data.dependencyIds.includes(sensorId)
          )
      )

      if (allDependenciesSelected) {
        sensorsToRender.push({
          id: derived.id,
          name: derived.data.name,
        })
      }
    })

    return sensorsToRender
  }, [selectedSensors, derivedValues])

  const selectedSensorEntries = useMemo(
    () =>
      enhancedSensorEntries.filter((sensor) =>
        selectedSensors.find(
          (selectedSensor) => selectedSensor.id === sensor.id
        )
      ),
    [enhancedSensorEntries, selectedSensors]
  )

  const selectedSensorsIds = useMemo(
    () =>
      selectedSensors
        .map((s) => s.id)
        .sort()
        .join(','),
    [selectedSensors]
  )

  useEffect(() => {
    prevSelectedSensorsRef.current = JSON.stringify(
      selectedSensors.map((s) => s.id).sort()
    )
  }, [selectedSensors])

  useEffect(() => {
    prevDerivedValuesRef.current = JSON.stringify(
      derivedValues.map((dv) => dv.id).sort()
    )
  }, [derivedValues])

  useEffect(() => {
    prevTimeRangeFromRef.current = timeRange.from
  }, [timeRange.from])

  // Track sensor selection changes - use length and IDs for stable comparison
  const prevSelectedSensorsIdsRef = React.useRef<string>('')

  useEffect(() => {
    if (
      selectedSensors.length > 0 &&
      selectedSensorsIds !== prevSelectedSensorsIdsRef.current
    ) {
      prevSelectedSensorsIdsRef.current = selectedSensorsIds
      trackEvent('Sensor Selection Changed', {
        sensors_selected_count: selectedSensors.length,
        selectedSensors: selectedSensors.map((s) => s.id),
        page: 'Asset Lens',
        tab: 'Analysis',
        analysis_type: 'time-series',
      })
    }
  }, [selectedSensorsIds, selectedSensors, trackEvent])

  return {
    isLiveTailRefresh,
    allSensorsToRender,
    selectedSensorEntries,
  }
}
